/**
 * steps/AccountDetailsStep.tsx
 * Account details step for the onboarding wizard
 *
 * Collects account information:
 * - Account name (required)
 * - Account number (optional)
 * - Account description (optional)
 * - Base currency (required, default: USD)
 * - Opening balance (optional, default: 0)
 * - Uses HeroUI inputs with built-in helper text (no FormField wrapper)
 * - Integrates with draft persistence system
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { User, Info } from 'lucide-react';

// 2. Internal absolute imports
import { Input, TextArea, Button, Card, CardBody, CardHeader, CardFooter, Accordion, AccordionItem } from '@enfomentis/ui';
import { CurrencyChooserModal } from '@/components/shared/CurrencyChooser/CurrencyChooserModal';
import { getCurrencyInfo } from '@/utils';
import { CURRENCY_DATABASE } from '@/constants';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';

/**
 * Account details step component
 */
export const AccountDetailsStep: React.FC = () => {
  const { draft, updateAccount } = useOnboardingData();

  // Local form state
  const [formData, setFormData] = useState({
    accountName: '',
    accountNumber: '',
    accountDescription: '',
    baseCurrency: 'USD',
    openingBalance: '0'
  });

  // Currency chooser modal state
  const [showCurrencyChooser, setShowCurrencyChooser] = useState(false);

  // Initialize from draft
  useEffect(() => {
    if (draft?.account) {
      setFormData({
        accountName: draft.account.accountName || '',
        accountNumber: draft.account.accountNumber || '',
        accountDescription: draft.account.accountDescription || '',
        baseCurrency: draft.account.baseCurrency || 'USD',
        openingBalance: draft.account.openingBalance !== undefined
          ? String(draft.account.openingBalance)
          : '0'
      });
    }
  }, [draft]);

  /**
   * Update draft with current form data
   */
  const updateDraft = (updates: Partial<typeof formData>): void => {
    const updatedFormData = { ...formData, ...updates };
    setFormData(updatedFormData);

    updateAccount({
      accountName: updatedFormData.accountName,
      accountNumber: updatedFormData.accountNumber,
      accountDescription: updatedFormData.accountDescription,
      baseCurrency: updatedFormData.baseCurrency,
      openingBalance: parseFloat(updatedFormData.openingBalance) || 0
    });
  };

  /**
   * Handle account name changes
   */
  const handleAccountNameChange = (value: string): void => {
    updateDraft({ accountName: value });
  };

  /**
   * Handle account number changes
   */
  const handleAccountNumberChange = (value: string): void => {
    updateDraft({ accountNumber: value });
  };

  /**
   * Handle account description changes
   */
  const handleDescriptionChange = (value: string): void => {
    updateDraft({ accountDescription: value });
  };

  /**
   * Handle base currency changes
   */
  const handleBaseCurrencyChange = (currencyCode: string): void => {
    updateDraft({ baseCurrency: currencyCode });
    setShowCurrencyChooser(false);
  };

  /**
   * Handle opening balance changes
   */
  const handleOpeningBalanceChange = (value: string): void => {
    updateDraft({ openingBalance: value });
  };

  // Get currency details for display
  const selectedCurrencyDetails = getCurrencyInfo(formData.baseCurrency, CURRENCY_DATABASE);
  const currencySymbol = selectedCurrencyDetails?.symbol || formData.baseCurrency;
  const currencyName = selectedCurrencyDetails?.name || formData.baseCurrency;

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-dvh w-full flex flex-col border-0">

      <CardHeader>
        <div className="flex items-center gap-2">
          <User className="w-5 h-5 text-primary" />
          <span className="text-large font-semibold">Account Details</span>
        </div>
      </CardHeader>

      <CardBody className="space-y-8">
        <div className="max-w-4xl mx-auto px-2">
          {/* Account Information Section */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold text-text-secondary uppercase tracking-wide">
              Account Information
            </h3>

            {/* Account Name & Number - Side by side on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Account Name */}
              <Input
                label="Account Name"
                labelPlacement='outside'
                placeholder="e.g., Main Trading Account"
                value={formData.accountName}
                onValueChange={handleAccountNameChange}
                isRequired
                variant="bordered"
                size="lg"
                classNames={{
                  input: "text-base"
                }}
                errorMessage={
                  formData.accountName.length > 0 && formData.accountName.trim().length < 2
                    ? "Account name must be at least 2 characters"
                    : undefined
                }
                isInvalid={formData.accountName.length > 0 && formData.accountName.trim().length < 2}
              />

              {/* Account Number */}
              <Input
                label="Account Number"
                labelPlacement='outside'
                placeholder="e.g., 12345678"
                value={formData.accountNumber}
                onValueChange={handleAccountNumberChange}
                variant="bordered"
                size="lg"
                classNames={{
                  input: "text-base"
                }}
              />
            </div>

            {/* Account Description - Full width */}
            <TextArea
              label="Description / Notes"
              labelPlacement='outside'
              placeholder="Describe your trading strategy, goals, or account purpose"
              value={formData.accountDescription}
              onValueChange={handleDescriptionChange}
              variant="bordered"
              size="lg"
              classNames={{
                input: "text-base"
              }}
              minRows={3}
              maxRows={6}
            />
          </div>

          {/* Financial Settings Section */}
          <div className="space-y-4 pt-4">
            <h3 className="text-md font-semibold text-text-secondary uppercase tracking-wide">
              Financial Settings
            </h3>

            {/* Opening Balance & Currency - Side by side on desktop, Balance first */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Opening Balance */}
              <Input
                label="Opening Balance"
                labelPlacement='outside'
                placeholder="0.00"
                value={formData.openingBalance}
                onValueChange={handleOpeningBalanceChange}
                type="number"
                step="0.01"
                isRequired
                variant="bordered"
                size="lg"
                classNames={{
                  input: "text-base"
                }}
                startContent={
                  <span className="text-default-400">
                    {currencySymbol}
                  </span>
                }
                errorMessage={
                  formData.openingBalance.length > 0 && (
                    isNaN(parseFloat(formData.openingBalance)) ||
                    parseFloat(formData.openingBalance) < 0
                  )
                    ? "Opening balance must be a valid non-negative number"
                    : undefined
                }
                isInvalid={
                  formData.openingBalance.length > 0 && (
                    isNaN(parseFloat(formData.openingBalance)) ||
                    parseFloat(formData.openingBalance) < 0
                  )
                }
              />

              {/* Base Currency */}
              <div className="-mt-1 flex flex-col">
                <label className="text-text-primary mb-1.5">
                  Base Currency <span className="text-danger">*</span>
                </label>
                <Button
                  variant="bordered"
                  size="lg"
                  className="w-full justify-start text-base font-normal border-default-200"
                  onPress={() => setShowCurrencyChooser(true)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{currencySymbol}</span>
                    <span>{currencyName}</span>
                    <span className="text-text-secondary">({formData.baseCurrency})</span>
                  </div>
                </Button>
              </div>
            </div>
          </div>

          {/* Validation Status */}
          {formData.accountName.trim().length >= 2 &&
           formData.openingBalance.length > 0 &&
           !isNaN(parseFloat(formData.openingBalance)) && (
            <div className="flex items-center gap-2 text-success text-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-success"></div>
              Account details look good! Ready to continue.
            </div>
          )}
        </div>
      </CardBody>

      <CardFooter className="shrink-0 border-t border-default-200">
        <Accordion variant="light" fullWidth isCompact>
          <AccordionItem
            key="why-needed"
            textValue="Why do we need this?"
            title={
              <div className="flex items-center gap-2">
                <Info size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">
                  Why do we need this?
                </span>
              </div>
            }
          >
            <ul className="text-sm text-text-secondary space-y-1 pb-2">
              <li>• Account name helps organize multiple trading accounts</li>
              <li>• Account number is optional - stored for your reference only</li>
              <li>• Opening balance is your current account balance</li>
              <li>• Base currency affects how all balances and P&L are calculated</li>
              <li>• All information stays private on your device</li>
            </ul>
          </AccordionItem>
        </Accordion>
      </CardFooter>

      {/* Currency Chooser Modal */}
      <CurrencyChooserModal
        isOpen={showCurrencyChooser}
        onClose={() => setShowCurrencyChooser(false)}
        selectedCurrency={formData.baseCurrency}
        onCurrencySelect={handleBaseCurrencyChange}
      />
    </Card>
  );
};